import { useState, useMemo } from "react";
import { ChevronDown, Bot, FileText, Mail, MessageCircle, Check } from "lucide-react";

const STATUSES = ["new", "contacted", "quoted", "closed"];

const STATUS_STYLES = {
  new: "border-ib-lime/40 text-ib-lime bg-ib-lime/10",
  contacted: "border-ib-blue/40 text-ib-blue bg-ib-blue/10",
  quoted: "border-ib-warning/40 text-ib-warning bg-ib-warning/10",
  closed: "border-ib-steel/40 text-ib-text-muted bg-ib-steel/10",
};
const NEXT = { new: "contacted", contacted: "quoted", quoted: "closed" };
const DETAIL_FIELDS = [
  ["customer_email", "Email"], ["customer_phone", "Phone"], ["grade", "Grade"], ["thickness", "Thickness"],
  ["width", "Width"], ["diameter", "Diameter"], ["length", "Length"], ["delivery_address", "Delivery"],
  ["pincode", "PIN"], ["delivery_timeline", "Timeline"], ["special_notes", "Notes"], ["updated_by", "Updated by"],
];

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export function EnquiryRow({ item, onStatus }) {
  const [open, setOpen] = useState(false);
  const visibleFields = useMemo(() => DETAIL_FIELDS.filter(([k]) => item[k]), [item]);
  const otherStatuses = useMemo(() => STATUSES.filter((s) => s !== item.status), [item.status]);
  const isBot = item.source === "chatbot_payal";
  const SourceIcon = isBot ? Bot : FileText;
  const next = NEXT[item.status];

  return (
    <div data-testid={`enquiry-row-${item.id}`} className="rounded-xl border border-ib-steel/20 bg-ib-surface/50 overflow-hidden">
      <div className="grid grid-cols-[auto_1fr_auto] md:grid-cols-[auto_2fr_2fr_1fr_auto_auto] gap-4 items-center p-4">
        <div className={`p-2 rounded-lg ${isBot ? "bg-ib-blue/10" : "bg-ib-lime/10"}`} title={isBot ? "Payal chatbot" : "Quote form"}>
          <SourceIcon className={`w-4 h-4 ${isBot ? "text-ib-blue" : "text-ib-lime"}`} />
        </div>
        <div className="min-w-0">
          <div className="font-outfit font-bold text-sm text-ib-text truncate" data-testid="enquiry-customer">{item.customer_name || "Anonymous visitor"}</div>
          <div className="font-manrope text-[11px] text-ib-text-muted truncate">{item.customer_email || item.customer_phone || fmt(item.created_at)}</div>
        </div>
        <div className="hidden md:block min-w-0">
          <div className="font-jetbrains text-xs text-ib-text truncate" data-testid="enquiry-material">{item.material || "—"} {item.grade ? `· ${item.grade}` : ""}</div>
          <div className="font-manrope text-[11px] text-ib-text-muted truncate">{item.quantity || "qty n/a"}{item.delivery_address ? ` → ${item.delivery_address}` : ""}</div>
        </div>
        <div className="hidden md:block font-manrope text-[11px] text-ib-text-muted">{fmt(item.created_at)}</div>
        <span data-testid="enquiry-status" className={`px-2.5 py-1 rounded-full border font-jetbrains text-[10px] capitalize ${STATUS_STYLES[item.status] || STATUS_STYLES.new}`}>
          {item.status}
        </span>
        <div className="flex items-center gap-1">
          {next && (
            <button data-testid={`mark-${next}-btn`} onClick={() => onStatus(item.id, next)}
              className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-ib-blue/90 hover:bg-ib-blue text-white font-outfit text-[11px] transition-colors">
              <Check className="w-3 h-3" /> Mark {next}
            </button>
          )}
          <button data-testid="enquiry-expand-btn" onClick={() => setOpen(!open)} className="p-2 text-ib-text-muted hover:text-ib-text">
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="enquiry-details" className="border-t border-ib-steel/15 p-4 grid grid-cols-2 md:grid-cols-4 gap-4 bg-ib-bg/30">
          {visibleFields.map(([k, label]) => (
            <div key={k}>
              <div className="font-manrope text-[10px] text-ib-text-muted uppercase tracking-wide">{label}</div>
              <div className="font-manrope text-xs text-ib-text mt-0.5 break-words">{String(item[k])}</div>
            </div>
          ))}
          <div className="col-span-2 md:col-span-4 flex flex-wrap items-center gap-4 pt-2 border-t border-ib-steel/10">
            <span className="inline-flex items-center gap-1 font-jetbrains text-[10px] text-ib-text-muted"><Mail className="w-3 h-3" /> Email alert: {item.email_sent ? "sent" : "not sent"}</span>
            <span className="inline-flex items-center gap-1 font-jetbrains text-[10px] text-ib-text-muted"><MessageCircle className="w-3 h-3" /> WhatsApp: {item.whatsapp_sent ? "sent" : "not sent"}</span>
            <div className="ml-auto flex gap-1">
              {otherStatuses.map((s) => (
                <button key={s} data-testid={`set-status-${s}`} onClick={() => onStatus(item.id, s)}
                  className="px-2.5 py-1 rounded-full border border-ib-steel/30 font-outfit text-[10px] capitalize text-ib-text-secondary hover:text-ib-text hover:border-ib-blue/40 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
